/* mcv3/configVisual.js — resolve a placement's OPTION SELECTIONS to the hex
 * colours the 3D scene paints with (2026-07-22 mandate: "when I change an
 * option the 3D representation must update"). ONE source of truth: the ids are
 * looked up in the SAME option library the panel renders (mcLibraryFromBlob —
 * technician catalog with allow-list gating), falling back to the static
 * MC_LIBRARY vocabulary for legacy ids. Pure; null hex = keep the shared
 * default material (no config → byte-identical scene). */
import { MC_LIBRARY } from "./mcLibrary";
import { resolveIdx, resolveTok } from "../../engine/catalogIds";
import { mcLibraryFromBlob } from "./mcCatalog";
import { fabricVisual } from "../../engine/fabrics";

const findHex = (lists, id) => {
  for (const l of lists) {
    const hit = (l || []).find((c) => c && c.id === id);
    if (hit && hit.hex) return hit.hex;
  }
  return null;
};
/* EXACT-IMAGE (2026-09-01): the entry's real-world coverage + rotation ride
 * the vis next to its photo, so both renderers repeat the photo at true mm
 * scale instead of the 600mm-square default (which stretched every
 * non-square image). Seeding mirrors exactTex.seedTexScale. */
const texOptsOf = (e) => {
  if (!e || !e.tex) return undefined;
  const px = Array.isArray(e.texPx) ? e.texPx : null;
  const sc = e.texScale > 0 ? e.texScale / 100 : 1;                  // "Texture scale %" (mandate §5)
  const w = (e.texWmm > 0 ? e.texWmm : 600) * sc;
  const h = (e.texHmm > 0 ? e.texHmm : (px && px[0] > 0 ? Math.round((e.texWmm > 0 ? e.texWmm : 600) * px[1] / px[0]) : (e.texWmm > 0 ? e.texWmm : 600))) * sc;
  const rot = ((Math.round((Number(e.texRot) || 0) / 90) * 90) % 360 + 360) % 360;
  return { tileWmm: w, tileHmm: h, rotDeg: rot };
};

const flatColors = (byType) => {
  const out = [];
  Object.keys(byType || {}).forEach((k) => { (byType[k] || []).forEach((c) => out.push(c)); });
  return out;
};

/* mcEnsureConfig's SEEDED defaults are not a user choice: selecting a cabinet
 * auto-writes them, and treating them as an override would permanently skip
 * the technician's GLB and tint untouched cabinets. Only a value DIFFERENT
 * from the seed counts as an explicit visual pick. */
const SEED = { carcass: "c-white", doorColor: "mat-white", handleColor: "hc-steel", plinthColor: "pc-black" };

export function resolveConfigVisual(blob, type, cfg) {
  const out = { carcHex: null, frontHex: null, handleHex: null, plinthHex: null, worktopHex: null, interiorHex: null };
  if (!cfg) return out;
  const M = mcLibraryFromBlob(blob, type);
  if (cfg.carcass && cfg.carcass !== SEED.carcass) {
    out.carcHex = findHex([M.carcass && M.carcass.colors, MC_LIBRARY.carcass.colors], cfg.carcass);
    // CARCASS FULLY CONNECTED (2026-08-15 mandate: "carcass color isn't
    // working and connected"): the technician's colour record carries more
    // than a hex — a Materials-library link (matId → real texture maps) or an
    // uploaded swatch photo (tex). Surface both so orbit, Present and the
    // hybrid GLB painter render the ACTUAL finish; carcHex stays the
    // guaranteed fallback.
    const cI = resolveIdx((blob || {}).colors, cfg.carcass, "carc");
    const cc = cI >= 0 ? (((blob || {}).colors || [])[cI] || null) : null;
    if (cc && cc.matId) out.materialByRole = { ...(out.materialByRole || {}), carcass: { materialId: cc.matId } };
    else if (cc && cc.tex) { out.carcTex = cc.tex; out.carcTexOpts = texOptsOf(cc); }
  }
  if (cfg.doorColor && cfg.doorColor !== SEED.doorColor) {
    const byType = (M.door && M.door.colorsByType && M.door.colorsByType[cfg.door]) || [];
    out.frontHex = findHex([
      byType,
      flatColors(M.door && M.door.colorsByType),
      flatColors(MC_LIBRARY.door.colorsByType),
    ], cfg.doorColor);
    // same connection for FRONT finishes: pgN-cK → the price-group door tile,
    // whose uploaded texture / Materials link must reach the render.
    const dm = /^pg(\d+)-c((?:u)?\d+)$/.exec(String(cfg.doorColor));
    if (dm) {
      const gg = ((blob || {}).groups || []).find((x) => x && parseInt(x.g, 10) === +dm[1]);
      const dI = gg ? resolveTok(gg.doors, dm[2]) : -1;
      const dd = dI >= 0 ? (gg.doors || [])[dI] : null;
      if (dd && dd.matId) out.materialByRole = { ...(out.materialByRole || {}), front: { materialId: dd.matId } };
      else if (dd && dd.tex) { out.frontTex = dd.tex; out.frontTexOpts = texOptsOf(dd); }
    }
  }
  if (cfg.handleColor && cfg.handleColor !== SEED.handleColor) {
    // per-handle finishes first (technician: Handles → + Colour), then the
    // flat legacy list — so a colour authored for THIS handle always wins
    const hByType = (M.handle && M.handle.colorsByType && M.handle.colorsByType[cfg.handle]) || [];
    out.handleHex = findHex([
      hByType,
      flatColors(M.handle && M.handle.colorsByType),
      M.handle && M.handle.colors,
      MC_LIBRARY.handle.colors,
    ], cfg.handleColor);
  }
  // the technician's per-handle 3D MODEL (Handles → Attach 3D): surfaced on
  // the visual so the ONE resolver can mount it on the handle anchors in
  // orbit, Present and the technician bench alike.
  if (cfg.handle) {
    const t = ((M.handle && M.handle.types) || []).find((x) => x && x.id === cfg.handle);
    if (t && t.model) out.handleModel = t.model;
    // ADMIN PLACEMENT (2026-08-10): the technician's exact-mm positions for
    // this handle. The customer's pick (cfg.handlePlacement) — or the FIRST
    // authored position as the default — is resolved to plain geometry the
    // scene builders apply verbatim. No placements authored → engine default.
    const pls = (t && t.placements) || [];
    if (pls.length) {
      const resolvePl = (id) => {
        const pick = pls.find((x) => x && x.id === id) || pls[0];
        return {
          v: pick.v || "top", vMm: Number(pick.vMm) || 0,
          h: pick.h || "edge", hMm: Number(pick.hMm) || 0,
          rot: Number(pick.rot) || 0,
        };
      };
      out.handlePlace = resolvePl(cfg.handlePlacement);
      // SMART PER-FRONT placements (2026-08-11): a module with several doors/
      // drawers can give EACH front its own admin-authored position —
      // cfg.handlePlacements = { "door-0": plId, "drawer-2": plId, … } keyed
      // by the engine's spec order. Only authored placement ids resolve; the
      // module-level pick stays the default for unlisted fronts.
      const per = cfg.handlePlacements;
      if (per && typeof per === "object") {
        const byFront = {};
        Object.keys(per).forEach((k) => {
          const id = per[k];
          if (typeof id === "string" && id && pls.some((x) => x && x.id === id)) byFront[k] = resolvePl(id);
        });
        if (Object.keys(byFront).length) out.handlePlaceByFront = byFront;
      }
    }
  }
  // GOLA (2026-08-15 "make the gola system very smart"): the chosen PROFILE
  // supplies its own swatch/texture; the shared Gola Colours pool
  // (cfg.golaColor = "gc-K") overrides it. Either one paints the gola part —
  // detected automatically in uploaded GLBs (classifier role "gola") or
  // mapped by the technician.
  if (cfg.gola && cfg.gola !== "None") {
    const gI = resolveIdx((blob || {}).gola, cfg.gola, "gola");
    const gp = gI >= 0 ? (((blob || {}).gola || [])[gI] || null) : null;
    if (gp && gp.sw) out.golaHex = gp.sw;
    if (gp && gp.tex) { out.golaTex = gp.tex; out.golaTexOpts = texOptsOf(gp); }
  }
  if (cfg.golaColor) {
    const hx = findHex([M.gola && M.gola.colors], cfg.golaColor);
    // golaPick: the customer EXPLICITLY chose a finish — only then may the
    // painters recolour an uploaded profile GLB (2026-08-20 user: "i changed
    // the color and still didnt work" — the GLB kept its baked material and
    // only the hidden fallback box was painted). Without the pick the upload
    // keeps its own authored material.
    if (hx) { out.golaHex = hx; out.golaPick = true; delete out.golaTex; delete out.golaTexOpts; }
  }
  const pc = cfg.plinth && cfg.plinth.color;
  if (pc && pc !== SEED.plinthColor) {
    out.plinthHex = findHex([M.plinth && M.plinth.colors, MC_LIBRARY.plinth.colors], pc);
    // PLINTH FULLY CONNECTED (2026-08-15): "pmc-<matIdx>-<colIdx>" → the
    // technician's plinth-material colour record; its uploaded texture rides
    // along so the plinth renders the real finish, not a flat hex.
    const pm = /^pmc-((?:u)?\d+)-((?:u)?\d+)$/.exec(String(pc));
    if (pm) {
      const mI = resolveTok((blob || {}).pmat, pm[1]);
      const rec = (mI >= 0 ? (((blob || {}).pmat) || [])[mI] : null) || {};
      const cI2 = resolveTok(rec.colors, pm[2]);
      const col = cI2 >= 0 ? ((rec.colors || [])[cI2] || null) : null;
      if (col && col.matId) out.materialByRole = { ...(out.materialByRole || {}), plinth: { materialId: col.matId } };
      else if (col && col.tex) { out.plinthTex = col.tex; out.plinthTexOpts = texOptsOf(col); }
    }
  }
  // worktop: a technician catalog worktop NAME tints the counter with its
  // first colour swatch; legacy static names (no catalog entry) stay null.
  const wt = cfg.countertop;
  if (wt && wt !== "None") {
    const w = ((blob || {}).worktops || []).find((x) => x && x.name === wt);
    const c0 = w && w.colors && w.colors[0];
    if (c0 && c0.sw) out.worktopHex = c0.sw;
    // colour imported FROM the Materials library -> real textured render.
    // Slot shape is { materialId } — effectiveMaterialIdForRole reads
    // slot.materialId, so the bare-string emission here never resolved (the
    // textured path was silently dead; hex tint only). Fixed 2026-08-15.
    if (c0 && c0.matId) out.materialByRole = { ...(out.materialByRole || {}), worktop: { materialId: c0.matId } };
    else if (c0 && c0.tex) { out.worktopTex = c0.tex; out.worktopTexOpts = texOptsOf(c0); }
  }
  // BACKSPLASH (2026-08-11): technician entry -> hex tint + (if the colour was
  // imported from Materials) the real textured material on the wall panel.
  const bsn = cfg.backsplash;
  if (bsn && bsn !== "None") {
    const b = ((blob || {}).backsplash || []).find((x) => x && x.name === bsn);
    const b0 = b && b.colors && b.colors[0];
    if (b0 && b0.sw) out.backsplashHex = b0.sw;
    if (b0 && b0.matId) out.materialByRole = { ...(out.materialByRole || {}), backsplash: { materialId: b0.matId } };
    else if (b0 && b0.tex) { out.backsplashTex = b0.tex; out.backsplashTexOpts = texOptsOf(b0); }
  }
  // interior finish (Phase 1, previously inert): recolours ONLY the mapped
  // INTERIOR bucket of a GLB. "Standard" keeps the manufacturer interior;
  // a carcass pick still never touches interior meshes (protected role).
  const inf = cfg.interiorFinish;
  if (inf === "Melamine White") out.interiorHex = "#F4F4F0";
  else if (inf === "Match Carcass" && out.carcHex) out.interiorHex = out.carcHex;
  // GLASS-AS-FRONT-STYLE (Phase 3): a front GROUP the technician flagged
  // `glass` renders transmissive fronts everywhere (procedural, painted onto
  // mapped GLB front meshes, Present path trace). Set only when true so the
  // base shape (and its pins) stays identical for non-glass configs.
  const gm = /^pg(\d+)/.exec(String(cfg.door || cfg.doorColor || ""));
  if (gm) {
    const gg = ((blob || {}).groups || []).find((x) => x && parseInt(x.g, 10) === +gm[1]);
    if (gg && gg.glass) out.frontGlass = true;
  }
  // FABRIC (batch 14, 2026-10-03): the Fabric Library colour of a sofa / armchair / lounger —
  // its hex, and its uploaded PHOTO (fabricTex + the mm it covers) for the upholstery meshes
  if (cfg.fabric && cfg.fabric !== "None") Object.assign(out, fabricVisual(blob, cfg.fabric));
  return out;
}

/** Any config-driven visual override present? (Drives the GLB→procedural swap
 *  decision: a GLB carries its own baked materials, so a colour choice must
 *  render procedurally to be visible — unless the module's model3d declares
 *  hybrid material slots, resolved by engine/modelResolver.) */
export function hasVisualOverride(vis) {
  return !!(vis && (vis.carcHex || vis.frontHex || vis.handleHex || vis.golaHex || vis.plinthHex
    || vis.worktopHex || vis.backsplashHex || vis.interiorHex || vis.frontGlass
    || vis.carcTex || vis.frontTex || vis.golaTex || vis.plinthTex
    || vis.worktopTex || vis.backsplashTex || vis.fabricHex || vis.fabricTex));
}
